import { Injectable } from '@angular/core';
import { UserProfile } from '@cpd-app/core-module/models';
import {
  AccountCreationPromptService,
  NavigationService,
  UserProfileDataService
} from '@cpd-app/core-module/services';
import { ProfileWorkTypeEnum } from '@cpd-app/generated';
import { AppState } from '@cpd-app/state';
import { LoaderFacade } from '@cpd-app/state/loader';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom
} from 'rxjs/operators';
import { ErrorReceived } from '../error/error.actions';

import { GoalFacade } from '../goal';
import { TrackerFacade } from '../tracker';
import * as UserProfileActions from './user-profile.actions';
import * as UserProfileSelectors from './user-profile.selectors';
import { selectUserProfile } from './user-profile.selectors';
import { selectAuthSharedToken } from '@cpd-app/state/auth/auth.selectors';

@Injectable()
export class UserProfileEffects {
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private navigationService: NavigationService,
    private userProfileDataService: UserProfileDataService,
    private goalFacade: GoalFacade,
    private trackerFacade: TrackerFacade,
    private accountCreationPromptService: AccountCreationPromptService,
    private loaderFacade: LoaderFacade
  ) {}

  // LOAD USER PROFILE
  loadUserProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.loadUserProfile),
      withLatestFrom(
        this.store.select(UserProfileSelectors.selectHasUserProfileBeenSaved),
        this.store.select(UserProfileSelectors.selectUserProfile)
      ),
      filter(([, hasUserProfileBeenSaved, userProfile]) => {
        return hasUserProfileBeenSaved === false || !userProfile?.userId;
      }),
      switchMap(() => {
        return this.userProfileDataService.getUserProfile().pipe(
          filter((userProfile) => !!userProfile.userId),
          map((userProfile) => {
            this.goalFacade.setReportDownloaded(userProfile.reportDownloaded);
            this.goalFacade.setReportShared(userProfile.reportShared);

            return UserProfileActions.userProfileLoadedSuccess({ userProfile });
          }),
          catchError((error: TypeError) =>
            of(UserProfileActions.userProfileLoadedFailure({ error }))
          )
        );
      })
    )
  );

  // LOAD SHARE USER PROFILE
  loadShareUserProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.loadShareUserProfile),
      withLatestFrom(this.store.select(selectAuthSharedToken)),
      filter(([, shareToken]) => !!shareToken),
      switchMap(([, token]) => {
        return this.userProfileDataService.getShareUserProfile(token).pipe(
          filter((userProfile) => !!userProfile.userId),
          map((userProfile) => {
            this.goalFacade.setReportDownloaded(userProfile.reportDownloaded);
            this.goalFacade.setReportShared(userProfile.reportShared);

            return UserProfileActions.userShareProfileLoadedSuccess({
              userProfile
            });
          }),
          catchError((error: TypeError) =>
            of(UserProfileActions.userShareProfileLoadedFailure({ error }))
          )
        );
      })
    )
  );

  // SAVE MILIRARY
  updateUserProfileFromMilitarySkills$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.updateUserProfileFromMilitarySkills),
      switchMap((action) => {
        return this.userProfileDataService
          .saveUserSkillsSurvey(action.userProfile)
          .pipe(
            map((userProfile: UserProfile) =>
              UserProfileActions.updateUserProfileFromMilitarySkillsSuccess({
                userProfile
              })
            ),
            catchError((error: TypeError) => of(ErrorReceived({ error })))
          );
      })
    )
  );

  saveMilitaryServeType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.saveMilitaryServeType),
      switchMap((action) => {
        return this.userProfileDataService
          .saveMilitaryServeType(action.serveTypeId)
          .pipe(
            map(({ serveTypeId }) =>
              UserProfileActions.saveMilitaryServeTypeSuccess({
                serveTypeId
              })
            ),
            catchError((error: TypeError) => of(ErrorReceived({ error })))
          );
      })
    )
  );

  saveMilitaryAirbaseLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.saveMilitaryAirbaseLocation),
      switchMap((action) => {
        return this.userProfileDataService
          .saveUserAirbaseLocation(action.airbaseLocationId)
          .pipe(
            map(({ userAirbaseLocation }) =>
              UserProfileActions.saveMilitaryAirbaseLocationSuccess({
                userAirbaseLocation
              })
            ),
            catchError((error: TypeError) => of(ErrorReceived({ error })))
          );
      })
    )
  );

  saveMilitarySpacebaseLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.saveMilitarySpacebaseLocation),
      switchMap((action) => {
        return this.userProfileDataService
          .saveUserSpacebaseLocation(action.spacebaseLocationId)
          .pipe(
            map(({ userSpacebaseLocation }) =>
              UserProfileActions.saveMilitarySpacebaseLocationSuccess({
                userSpacebaseLocation
              })
            ),
            catchError((error: TypeError) => of(ErrorReceived({ error })))
          );
      })
    )
  );

  // DATA-1: all-branch installation answer (flag-gated on both ends)
  saveMilitaryInstallation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.saveMilitaryInstallation),
      switchMap((action) => {
        return this.userProfileDataService
          .saveUserInstallation(action.installationId)
          .pipe(
            map(({ userInstallation }) =>
              UserProfileActions.saveMilitaryInstallationSuccess({
                userInstallation
              })
            ),
            catchError((error: TypeError) => of(ErrorReceived({ error })))
          );
      })
    )
  );

  // SAVE USER SKILLS SURVEY TO DATABASE
  saveUserSkillsSurvey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.saveUserSkillsSurvey),
      switchMap((action) => {
        return this.userProfileDataService
          .saveUserSkillsSurvey(action.userProfile)
          .pipe(
            switchMap((userProfile: UserProfile) => {
              return [
                UserProfileActions.userSkillsSurveySavedSuccess({
                  userProfile
                })
              ];
            }),
            // Both: the generic reporter as before, and a failure the survey
            // itself can see. Without the second one a denied save was
            // silent, because the toast suppresses authorization errors.
            catchError((error: TypeError) => [
              ErrorReceived({ error }),
              UserProfileActions.userSkillsSurveySavedFailure()
            ])
          );
      })
    )
  );

  // SAVE USER INTERESTS SURVEY TO DATABASE
  saveUserInterestsSurvey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.saveUserInterestsSurvey),
      withLatestFrom(this.trackerFacade.interestSurveyTracker$),
      switchMap(([action, tracker]) => {
        return this.userProfileDataService
          .saveUserInterestsSurvey(
            action.userProfile.userInterests,
            tracker?.id
          )
          .pipe(
            map((userProfile: UserProfile) =>
              UserProfileActions.userInterestsSurveySavedSuccess({
                userProfile
              })
            ),
            catchError((error: TypeError) => of(ErrorReceived({ error })))
          );
      })
    )
  );

  // SAVE USER WORK VALUES TO DATABASE
  saveUserWorkValuesSurvey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.saveUserWorkValuesSurvey),
      switchMap((action) => {
        return this.userProfileDataService
          .saveUserWorkValues(action.userProfile.userWorkValues)
          .pipe(
            map((userProfile: UserProfile) =>
              UserProfileActions.userWorkValuesSurveySavedSuccess({
                userProfile
              })
            ),
            catchError((error: TypeError) => of(ErrorReceived({ error })))
          );
      })
    )
  );

  // SAVE USER LIFESTYLE TO DATABASE
  saveUserLifestyleSurvey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.saveUserLifestyleSurvey),
      switchMap((action) => {
        return this.userProfileDataService
          .saveUserLifestyleSurvey(
            action.userProfile.userAspirations,
            action.userProfile.userPreferredLocations,
            action.userProfile.userCurrentLocations
          )
          .pipe(
            map((userProfile: UserProfile) =>
              UserProfileActions.userLifestyleSurveySavedSuccess({
                userProfile
              })
            ),
            catchError((error: TypeError) => of(ErrorReceived({ error })))
          );
      })
    )
  );

  // SAVE USER PRE GOALS
  saveUserPreGoals$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.saveUserPreGoals),
      withLatestFrom(this.store.select(selectUserProfile)),
      // `userProfile?.userId`: reading `.userId` off a null profile threw
      // outside the inner catchError, which errored the effect's source stream
      // for the rest of the session. Pre-goals then stopped saving silently.
      switchMap(([{ values, surveyType }, userProfile]) =>
        this.userProfileDataService
          .saveUsePreGoals({ ...values, userId: userProfile?.userId })
          .pipe(
            map(() =>
              UserProfileActions.saveUserPreGoalsSuccess({ surveyType })
            ),
            catchError((error: TypeError) => of(ErrorReceived({ error })))
          )
      )
    )
  );

  // SAVE USER SCHOOL GOALS
  saveUserSchoolGoals$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.saveUserSchoolGoals),
      withLatestFrom(this.store.select(selectUserProfile)),
      switchMap(([{ values, surveyType }]) =>
        this.userProfileDataService.saveUserSchoolGoals({ ...values }).pipe(
          map(() =>
            UserProfileActions.saveUserSchoolGoalsSuccess({ surveyType })
          ),
          catchError((error: TypeError) => of(ErrorReceived({ error })))
        )
      )
    )
  );
  // SAVE USER SCHOOL GOALS
  saveUserProgramGoals$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.saveUserProgramGoals),
      withLatestFrom(this.store.select(selectUserProfile)),
      switchMap(([{ values, surveyType }]) =>
        this.userProfileDataService.saveUserProgramGoals({ ...values }).pipe(
          map(() =>
            UserProfileActions.saveUserProgramGoalsSuccess({ surveyType })
          ),
          catchError((error: TypeError) => of(ErrorReceived({ error })))
        )
      )
    )
  );
  saveUserWorkTransition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.updateUserProfileWorkTransition),
      withLatestFrom(this.store.select(selectUserProfile)),
      switchMap(([{ monthlyTransition }]) =>
        this.userProfileDataService
          .saveUserWorkTransition({
            numberOfMonthsOfTermination: monthlyTransition
          })
          .pipe(
            map((userProfile) =>
              UserProfileActions.updateUserProfileWorkTransitionSuccess({
                userProfile
              })
            ),
            catchError((error: TypeError) => of(ErrorReceived({ error })))
          )
      )
    )
  );

  saveUserMilitaryTransition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.updateUserProfileMilitaryTransition),
      withLatestFrom(this.store.select(selectUserProfile)),
      switchMap(([{ monthlyTransition }]) =>
        this.userProfileDataService
          .saveUserMilitaryTransition({
            numberOfMonthsOfTermination: monthlyTransition
          })
          .pipe(
            map((userProfile) =>
              UserProfileActions.updateUserProfileMilitaryTransitionSuccess({
                userProfile
              })
            ),
            catchError((error: TypeError) => of(ErrorReceived({ error })))
          )
      )
    )
  );

  // Init User Match
  initUserMatches$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.initUserMatches),
      switchMap(() =>
        this.userProfileDataService.initUserMatches().pipe(
          map(() => UserProfileActions.initUserMatchesSuccess()),
          catchError((error: TypeError) => of(ErrorReceived({ error })))
        )
      )
    )
  );

  // REDIRECT USER AFTER PRE GOALS HAS BEEN SET
  saveUserPreGoalsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(ofType(UserProfileActions.saveUserPreGoalsSuccess)),
    { dispatch: false }
  );

  // SAVE USER SURVEY SUCCESS
  userSurveySavedSuccess$ = createEffect(() =>
    this.actions$.pipe(
      /*
       * INTERESTS BELONGS HERE TOO, AND WAS MISSING.
       *
       * Three of the four surveys were listed. Finishing the interests survey
       * therefore did none of what this effect does: it did not route back to
       * Build Your Target, it did not re-read the profile, and it did not
       * recalculate the matches. That is the other half of why an account with
       * all four surveys Done carries an interests quality id of null on every
       * occupation card.
       */
      ofType(
        UserProfileActions.userSkillsSurveySavedSuccess,
        UserProfileActions.userInterestsSurveySavedSuccess,
        UserProfileActions.userWorkValuesSurveySavedSuccess,
        UserProfileActions.userLifestyleSurveySavedSuccess
      ),
      tap((action) => {
        if (
          action.type === UserProfileActions.userSkillsSurveySavedSuccess.type
        ) {
          this.accountCreationPromptService.openAccountCreationDialog(
            'skills-survey'
          );
        }

        /*
         * EVERY SURVEY BUT INTERESTS.
         *
         * This line sat outside the `if` above, so it fired for all four, and
         * for interests that meant navigating away from the page the member
         * had just earned. The interests container swaps itself to the radar
         * results view the moment `isInterestsSurveyComplete` turns true, and
         * this navigation ran first: the results rendered and were replaced by
         * the home page before anybody saw them. Reaching them later through
         * Retake worked only because that route does not come through here,
         * which is what made the results look like they existed sometimes.
         *
         * Interests is the only one of the four with a results view of its
         * own. Work values ends on its sorting step and lifestyle on its last
         * question, so for those three Build Your Target IS the next screen
         * and they still go there.
         */
        if (
          action.type !==
          UserProfileActions.userInterestsSurveySavedSuccess.type
        ) {
          this.navigationService.goToBuildYourTargetPage();
        }
      }),
      /*
       * RE-READ THE PROFILE AFTER A SURVEY SAVES.
       *
       * The four surveys all write the same profile, and each one built its
       * payload from whatever its own component held when it mounted. So
       * finishing one and starting the next meant the second working from a
       * copy that predated the first, and its save then wrote that stale copy
       * back: the divergence the PWS diagnostic describes as the browser's
       * profile drifting from the database.
       *
       * The server's answer is the one that counts, so it is fetched rather
       * than assumed. This is also why the response matters: the save effects
       * already return the saved profile, but nothing downstream re-read it
       * before the next survey started.
       */
      switchMap(() => [
        UserProfileActions.userCompletesAnySurvey(),
        /*
         * NO `initUserMatches` HERE. IT IS NOT A RECALCULATION.
         *
         * I added it on the strength of its name and paid for it. Read what it
         * does, in `CalculateUserMatchesService`:
         *
         *     DELETE FROM API.UserMatches WHERE UserId = ...
         *     call api.initusermatches(userId)
         *
         * and `api.initusermatches` (migration 20250218221502) inserts bare
         * rows carrying UserId, OnetCode and IndustryCode only. Every one of
         * the four quality ids is left NULL. It is an INITIALISER: it seeds
         * empty rows so the survey procedures have something to UPDATE. It
         * computes nothing.
         *
         * So dispatching it after a survey saved meant: the survey's own
         * procedure computes its dimension, and this immediately throws all
         * four away. Finishing interests turned every card green and blanked
         * the rest; finishing lifestyle turned them all purple. Each survey
         * replaced the last instead of adding to it.
         *
         * Nothing needs to be dispatched here. Each survey service already
         * calls its own `calculateMatches` and rebuilds the occupation cards
         * from what it wrote.
         */
        UserProfileActions.loadUserProfile()
      ])
    )
  );

  // ACCEPT PRIVACY POLICY
  acceptPrivacyPolicy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.acceptPrivacyPolicy),
      switchMap(() => this.userProfileDataService.acceptPrivacyPolicy(true)),
      mergeMap((data) => [
        UserProfileActions.acceptPrivacyPolicySuccess(),
        UserProfileActions.userProfileLoadedSuccess({
          userProfile: data.profile
        })
      ]),
      catchError((error) => of(ErrorReceived({ error })))
    )
  );

  acceptPrivacyPolicySuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserProfileActions.acceptPrivacyPolicySuccess),
        tap(() => this.navigationService.goToDirectLink())
      ),
    { dispatch: false }
  );
  acceptBannerPrivacyPolicy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.acceptBannerPrivacyPolicy),
      withLatestFrom(
        this.store.select(UserProfileSelectors.selectProfileWorkType)
      ),
      switchMap(([, { type }]) =>
        this.userProfileDataService.acceptProfileWorkType({
          type,
          servicepolicyaccepted: true
        })
      ),
      mergeMap((data) => [
        UserProfileActions.acceptBannerPrivacyPolicySuccess(),
        UserProfileActions.userProfileLoadedSuccess({
          userProfile: data.profile
        })
      ]),
      catchError((error) => of(ErrorReceived({ error })))
    )
  );

  acceptBannerPrivacyPolicySuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserProfileActions.acceptBannerPrivacyPolicySuccess),
        tap(() => this.navigationService.goToDirectLink())
      ),
    { dispatch: false }
  );

  updateProfileWorkType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.updateProfileWorkType),
      switchMap(({ workType }) => {
        if (workType === ProfileWorkTypeEnum.FederalEmployeeOrServicemember) {
          return this.userProfileDataService.acceptProfileWorkType({
            type: workType
          });
        }
        return this.userProfileDataService.acceptProfileWorkType({
          type: workType,
          servicepolicyaccepted: true
        });
      }),
      mergeMap((data) => [
        UserProfileActions.updateProfileWorkTypeSuccess({
          profileWorkType: data.profile.profileWorkType
        }),
        UserProfileActions.userProfileLoadedSuccess({
          userProfile: data.profile
        })
      ]),
      catchError((error) => {
        this.loaderFacade.hideLoader();
        return of(ErrorReceived({ error }));
      })
    )
  );

  updateProfileWorkTypeSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserProfileActions.updateProfileWorkTypeSuccess),
        tap(() => this.navigationService.goToDirectLink())
      ),
    { dispatch: false }
  );
}
